/**
 * Force + Kahuna shared indicators (JS Combined Dots) — daily and weekly,
 * configurable, with precomputed defaults.
 *
 *   Force(N) = sign of (close − EMA(close, N))   → Long / Short
 *   Kahuna   = direction from the day-over-day change in Bollinger %B
 *              (length, mult), banded: Big |Δ%B| > threshold, Small in a band.
 *
 * Each weekly variant runs the SAME math on ISO-week closes (last close per
 * week, in-progress week inclusive). Default-param values are baked into
 * `scanner_panel` (force2 / force13 / kahuna_dpctb + _w): a default column reads
 * that field instant (Force via `panelKey`; Kahuna via the frontend derive in
 * dynamicIndicatorColumns), and any changed param recomputes via this series.
 */
import { familyGroup } from "../indicatorGroups";
import { TF_DW, TF_PARAM } from "./timeframe";
import type { OHLCV, IndicatorParams, SharedParam, SharedIndicatorDef } from "../types";
import { emaFull, smaFull, stdevFull } from "./ta";
import { resampleByLastClose, htIndexPerDay } from "./weekly";
import { ageSeries } from "./age";

const len = (v: number | undefined, d: number) =>
  Math.max(1, Math.round(typeof v === "number" && Number.isFinite(v) ? v : d));
const numOr = (v: number | undefined, d: number) =>
  typeof v === "number" && Number.isFinite(v) ? v : d;

const GROUP = familyGroup("dots"); // folds into "MarketPulse Indicators" in the picker
const LONG_SHORT = { pos: "Buy", neg: "Sell", zero: "—" } as const; // Force = Buy / Sell
const UP_DOWN = { pos: "Up", neg: "Down", zero: "—" } as const; // Big / Small Kahuna

/** Project a weekly series onto daily bars (each day → the week it belongs to). */
function projectWeekly(
  data: OHLCV,
  minWeeks: number,
  weeklyFromCloses: (wkCloses: number[]) => (number | null)[],
): (number | null)[] {
  const { times, closes } = data;
  const n = closes.length;
  if (!times || times.length !== n) return new Array(n).fill(null);
  const { htTimes, htCloses } = resampleByLastClose(times, closes, "week");
  if (htCloses.length < minWeeks) return new Array(n).fill(null);
  const w = weeklyFromCloses(htCloses);
  const wk = htIndexPerDay(times, htTimes);
  return times.map((_, i) => {
    const k = wk[i]!;
    return k >= 0 && k < w.length ? w[k]! : null;
  });
}

// ── Force = close − EMA(close, N) (signed; sign → Long/Short) ─────────
// Returns the RAW signed distance (matches the precomputed force2/force13
// field, so default == panelKey and sorting is by magnitude); the `state`
// format renders its sign as Long / Short.
function forceSeries(closes: ReadonlyArray<number>, n: number): (number | null)[] {
  const ema = emaFull(closes, n);
  return closes.map((c, i) =>
    Number.isFinite(c) && Number.isFinite(ema[i]!) ? c - ema[i]! : null,
  );
}

// ── Kahuna = direction from Δ Bollinger %B (length, mult), banded ─────
function dpctbSeries(closes: ReadonlyArray<number>, length: number, mult: number): (number | null)[] {
  const mid = smaFull(closes, length);
  const sd = stdevFull(closes, length);
  const pctb = closes.map((c, i) => {
    const up = mid[i]! + mult * sd[i]!;
    const lo = mid[i]! - mult * sd[i]!;
    return Number.isFinite(c) && up !== lo ? (c - lo) / (up - lo) : null;
  });
  return pctb.map((p, i) => (i > 0 && p != null && pctb[i - 1] != null ? p - pctb[i - 1]! : null));
}

/** +1 / -1 / 0 from a Δ%B series, banded by [lo, hi] (hi = Infinity for Big). */
function bandState(dpctb: (number | null)[], lo: number, hi: number): (number | null)[] {
  return dpctb.map((dp) => {
    if (dp == null) return null;
    if (dp >= lo && dp <= hi) return 1;
    if (dp <= -lo && dp >= -hi) return -1;
    return 0;
  });
}

/** HGSI's Kahuna bands on the one-day change in %B: Little (Small) from 0.24,
 *  Big from 0.40 (the deck's "Reading the %B Band in Practice"). */
export const KAHUNA_SMALL = 0.24;
export const KAHUNA_BIG = 0.4;

export type KahunaKind = "Big up" | "Small up" | "Small down" | "Big down";
export type KahunaRead = { dPctB: number; kahuna: KahunaKind | null };

/** The NEWEST bar's Kahuna on the default bands (Bollinger 20, 2): the change
 *  in %B since the bar before, and Big / Small up or down, or null when it
 *  moved less than 0.24. Null when the bands have not formed on the newest
 *  bar. The same Δ%B the Big / Small Kahuna columns band. */
export function kahunaOfCloses(closes: ReadonlyArray<number>): KahunaRead | null {
  const d = dpctbSeries(closes, 20, 2);
  const v = d.length ? d[d.length - 1] : null;
  if (v == null || !Number.isFinite(v)) return null;
  const a = Math.abs(v);
  const size = a >= KAHUNA_BIG ? "Big" : a >= KAHUNA_SMALL ? "Small" : null;
  return { dPctB: v, kahuna: size ? (`${size} ${v > 0 ? "up" : "down"}` as KahunaKind) : null };
}

const lastFinite = (a: (number | null)[]): number | null => {
  for (let i = a.length - 1; i >= 0; i--) {
    const v = a[i];
    if (v != null && Number.isFinite(v)) return v;
  }
  return null;
};

/** Latest Force(N) value (close − EMA(close, N)), daily or weekly. The worker
 *  bakes this as `force2`/`force13`(+`_w`), so the column default reads it back
 *  instant — same function the configurable indicator recomputes with. */
export function forceValue(data: OHLCV, n: number, weekly: boolean): number | null {
  return lastFinite(weekly ? projectWeekly(data, n + 1, (wk) => forceSeries(wk, n)) : forceSeries(data.closes, n));
}

/** Latest Kahuna Δ%B value (length, mult), daily or weekly — baked as
 *  `kahuna_dpctb`(+`_w`); Big/Small Kahuna threshold it. */
export function kahunaDpctbValue(data: OHLCV, length: number, mult: number, weekly: boolean): number | null {
  const f = (closes: number[]) => dpctbSeries(closes, length, mult);
  return lastFinite(weekly ? projectWeekly(data, length + 1, f) : f([...data.closes]));
}

// ── Defs ─────────────────────────────────────────────────────────────
// One def per concept + a Timeframe select (default Daily). panelKey = the daily
// baked column; Weekly reads the _w column instantly via the tf-resolver.
function forceDef(id: string, label: string, dflt: number, panelKey: string): SharedIndicatorDef {
  return {
    id, label, group: GROUP, kind: "state", format: "state", stateLabels: LONG_SHORT, panelKey,
    params: [TF_DW, { id: "length", label: "EMA length", kind: "number", default: dflt, min: 1, max: 200, step: 1 }],
    computeSeries: (d: OHLCV, p: IndicatorParams) => {
      const n = len(p.length, dflt);
      return p[TF_PARAM] === 1 ? projectWeekly(d, n + 1, (wk) => forceSeries(wk, n)) : forceSeries(d.closes, n);
    },
  };
}

const BB_PARAMS: ReadonlyArray<SharedParam> = [
  { id: "length", label: "BB length", kind: "number", default: 20, min: 2, max: 100, step: 1 },
  { id: "mult", label: "BB stdev", kind: "number", default: 2, min: 0.5, max: 5, step: 0.1 },
];

/** Banded Kahuna state (+1 / -1 / 0) from Δ%B, daily or weekly — the single
 *  source the signal def and its age companion both read. */
function kahunaBandSeries(
  d: OHLCV, p: IndicatorParams, weekly: boolean,
  bandFrom: (p: IndicatorParams) => { lo: number; hi: number },
): (number | null)[] {
  const length = len(p.length, 20), mult = numOr(p.mult, 2);
  const b = bandFrom(p);
  const f = (closes: number[]) => bandState(dpctbSeries(closes, length, mult), b.lo, b.hi);
  return weekly ? projectWeekly(d, length + 1, f) : f([...d.closes]);
}

function kahunaDef(
  id: string, label: string,
  extraParams: ReadonlyArray<SharedParam>,
  bandFrom: (p: IndicatorParams) => { lo: number; hi: number },
): SharedIndicatorDef {
  return {
    id, label, group: GROUP, kind: "state", format: "state", stateLabels: UP_DOWN,
    params: [TF_DW, ...BB_PARAMS, ...extraParams],
    computeSeries: (d: OHLCV, p: IndicatorParams) => kahunaBandSeries(d, p, p[TF_PARAM] === 1, bandFrom),
  };
}

const bigBand = (p: IndicatorParams) => ({ lo: numOr(p.threshold, KAHUNA_BIG), hi: Infinity });
const smallBand = (p: IndicatorParams) => ({ lo: numOr(p.inner, KAHUNA_SMALL), hi: numOr(p.outer, KAHUNA_BIG) });
const BIG_PARAMS: ReadonlyArray<SharedParam> = [
  { id: "threshold", label: "Big threshold", kind: "number", default: 0.4, min: 0.1, max: 2, step: 0.05 },
];
const SMALL_PARAMS: ReadonlyArray<SharedParam> = [
  { id: "inner", label: "Band inner", kind: "number", default: 0.24, min: 0.05, max: 1, step: 0.02 },
  { id: "outer", label: "Band outer", kind: "number", default: 0.4, min: 0.1, max: 2, step: 0.05 },
];

export const FORCE2 = forceDef("mpp-force2", "Afiat - EMA 2 Bias", 2, "force2");
export const FORCE13 = forceDef("mpp-force13", "Afiat - EMA 13 Bias", 13, "force13");
export const BIG_KAHUNA = kahunaDef("mpp-big-kahuna", "Afiat - %B Thrust Large", BIG_PARAMS, bigBand);
export const SMALL_KAHUNA = kahunaDef("mpp-small-kahuna", "Afiat - %B Thrust Small", SMALL_PARAMS, smallBand);

// ── Age companions ───────────────────────────────────────────────────
// Force is a persistent state (close above / below its EMA) → bars the SIGN
// has held. Kahuna is an event (Δ%B crossing its band) → bars since the last
// fire. Each carries a `panelKey` so the default-param column reads the baked
// scanner_panel field; a changed param recomputes via the same series.
const signOf = (v: number | null): number | null => (v == null ? null : v > 0 ? 1 : v < 0 ? -1 : 0);
const stateAgeNZ = (s: (number | null)[]): (number | null)[] => {
  const a = ageSeries(s, "state");
  for (let i = 0; i < s.length; i++) if (!s[i]) a[i] = null; // blank on neutral
  return a;
};

function forceAgeDef(id: string, label: string, dflt: number, panelKey: string): SharedIndicatorDef {
  return {
    id, label, group: GROUP, kind: "age", format: "int", panelKey,
    params: [TF_DW, { id: "length", label: "EMA length", kind: "number", default: dflt, min: 1, max: 200, step: 1 }],
    computeSeries: (d: OHLCV, p: IndicatorParams) => {
      const n = len(p.length, dflt);
      const f = p[TF_PARAM] === 1 ? projectWeekly(d, n + 1, (wk) => forceSeries(wk, n)) : forceSeries(d.closes, n);
      return stateAgeNZ(f.map(signOf));
    },
  };
}

function kahunaAgeDef(
  id: string, label: string,
  extraParams: ReadonlyArray<SharedParam>,
  bandFrom: (p: IndicatorParams) => { lo: number; hi: number },
  panelKey: string,
): SharedIndicatorDef {
  return {
    id, label, group: GROUP, kind: "age", format: "int", panelKey,
    params: [TF_DW, ...BB_PARAMS, ...extraParams],
    computeSeries: (d: OHLCV, p: IndicatorParams) =>
      ageSeries(kahunaBandSeries(d, p, p[TF_PARAM] === 1, bandFrom), "fire"),
  };
}

export const FORCE2_AGE = forceAgeDef("mpp-force2-age", "Afiat - EMA 2 Bias Age", 2, "force2_age");
export const FORCE13_AGE = forceAgeDef("mpp-force13-age", "Afiat - EMA 13 Bias Age", 13, "force13_age");
export const BIG_KAHUNA_AGE = kahunaAgeDef("mpp-big-kahuna-age", "Afiat - %B Thrust Large Age", BIG_PARAMS, bigBand, "big_kahuna_age");
export const SMALL_KAHUNA_AGE = kahunaAgeDef("mpp-small-kahuna-age", "Afiat - %B Thrust Small Age", SMALL_PARAMS, smallBand, "small_kahuna_age");

export const FORCE_KAHUNA_INDICATORS: ReadonlyArray<SharedIndicatorDef> = [
  FORCE2, FORCE2_AGE, FORCE13, FORCE13_AGE,
  BIG_KAHUNA, BIG_KAHUNA_AGE, SMALL_KAHUNA, SMALL_KAHUNA_AGE,
];
